import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useRef, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../../../app/redo-admin-rpc-client-provider";

interface TeamOption {
  _id: string;
  name: string;
}

export const CopyLatestReturnFlowSettings = memo(
  function CopyLatestReturnFlowSettings({
    sourceTeamId,
  }: {
    sourceTeamId: string;
  }) {
    const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
    const [destinationTeamId, setDestinationTeamId] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [teamOptions, setTeamOptions] = useState<TeamOption[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState<{
      type: "success" | "error";
      text: string;
    } | null>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout>();
    const isSelectingRef = useRef(false);

    const searchTeams = useHandler(async (query: string) => {
      if (query.trim().length < 2) {
        setTeamOptions([]);
        setShowDropdown(false);
        return;
      }

      setIsSearching(true);
      try {
        const result = await rpcClient.searchTeams({
          query: query.trim(),
          limit: 10,
        });
        setTeamOptions(result.teams);
        setShowDropdown(true);
      } catch (error) {
        console.error("Team search failed:", error);
        setTeamOptions([]);
        setShowDropdown(false);
      } finally {
        setIsSearching(false);
      }
    });

    useEffect(() => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Don't search if we're in the middle of a programmatic selection
      if (isSelectingRef.current) {
        return;
      }

      if (searchQuery.trim().length >= 2) {
        searchTimeoutRef.current = setTimeout(async () => {
          await searchTeams(searchQuery);
        }, 300); // Debounce for 300ms
      } else {
        setTeamOptions([]);
        setShowDropdown(false);
      }

      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }, [searchQuery, searchTeams]);

    const handleTeamSelect = useHandler((team: TeamOption) => {
      isSelectingRef.current = true; // Mark that we're doing a programmatic update
      setDestinationTeamId(team._id);
      setSearchQuery(`${team.name} (${team._id})`);
      setShowDropdown(false);

      // Reset the flag in the next event loop to ensure the effect has completed
      requestAnimationFrame(() => {
        isSelectingRef.current = false;
      });
    });

    const handleInputChange = useHandler((value: string) => {
      setSearchQuery(value);

      // Extract team ID from the input value
      const parenthesesMatch = value.match(/\(([^)]+)\)/);

      if (parenthesesMatch && parenthesesMatch[1]) {
        // If input contains parentheses, extract the ID from within them
        setDestinationTeamId(parenthesesMatch[1].trim());
      } else if (value.trim()) {
        // If no parentheses but input is not empty, treat as raw team ID
        setDestinationTeamId(value.trim());
      } else {
        // Clear the team ID if input is empty
        setDestinationTeamId("");
      }
    });

    const handleCopy = useHandler(async () => {
      setIsLoading(true);
      setMessage(null);

      try {
        const result = await rpcClient.copyLatestReturnFlow({
          sourceTeamId,
          destinationTeamId: destinationTeamId.trim(),
        });

        setMessage({
          type: result.success ? "success" : "error",
          text: result.message || "Operation completed",
        });

        if (result.success) {
          // Reset form on success
          setDestinationTeamId("");
          setSearchQuery("");
          setShowDropdown(false);
        }
      } catch (error) {
        setMessage({
          type: "error",
          text: `Failed to copy return flow: ${
            error instanceof Error ? error.message : "Unknown error"
          }`,
        });
      } finally {
        setIsLoading(false);
      }
    });

    const isFormValid = destinationTeamId.trim() !== "";

    return (
      <Flex dir="column">
        <Text fontSize="sm" textColor="secondary">
          Copy and publish the most recently published return flow from this
          team to another team
        </Text>

        <div style={{ position: "relative" }}>
          <RedoTextInput
            label="Destination Team"
            placeholder="Search by team name or enter team ID..."
            setValue={handleInputChange}
            value={searchQuery}
          />
          {isSearching && (
            <Text fontSize="xs" textColor="secondary">
              Searching teams...
            </Text>
          )}
          {showDropdown && teamOptions.length > 0 && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                backgroundColor: "white",
                border: "1px solid #e1e5e9",
                borderRadius: "4px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                zIndex: 1000,
                maxHeight: "200px",
                overflowY: "auto",
              }}
            >
              {teamOptions.map((team) => (
                <div
                  key={team._id}
                  onClick={() => handleTeamSelect(team)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f8f9fa";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "white";
                  }}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    borderBottom: "1px solid #f5f5f5",
                  }}
                >
                  <Text fontSize="sm" fontWeight="medium">
                    {team.name}
                  </Text>
                  <Text fontSize="xs" textColor="secondary">
                    {team._id}
                  </Text>
                </div>
              ))}
            </div>
          )}
        </div>
        {searchQuery.trim() !== "" &&
          !showDropdown &&
          teamOptions.length === 0 &&
          !isSearching && (
            <Text fontSize="xs" textColor="secondary">
              No teams found matching your search.
            </Text>
          )}

        <Flex>
          <RedoButton
            disabled={!isFormValid || isLoading}
            hierarchy="primary"
            onClick={handleCopy}
            text={isLoading ? "Copying..." : "Copy and Publish Return Flow"}
          />
        </Flex>

        {message && (
          <Text
            fontSize="sm"
            textColor={message.type === "success" ? "success" : "error"}
          >
            {message.text}
          </Text>
        )}
      </Flex>
    );
  },
);
